import type { MaybeRefOrGetter, Ref } from "vue";
import type { DialogStepperItemRef, DialogStepperRailVariant } from "./types";
import { createInjectionState } from "@vueuse/core";
import { computed, ref, toValue } from "vue";

const [useProvideDialogStepperState, useDialogStepperStateRaw] = createInjectionState(({
  modelValue,
  totalSteps,
  stepLabels,
  railInteractive,
  railVariant,
}: {
  modelValue: Ref<number>
  totalSteps: MaybeRefOrGetter<number>
  stepLabels?: MaybeRefOrGetter<string[] | undefined>
  railInteractive?: MaybeRefOrGetter<boolean>
  railVariant?: MaybeRefOrGetter<DialogStepperRailVariant | undefined>
}) => {
  // Read through a computed so a wizard whose steps depend on form state or permissions keeps the
  // progress dots and the next/previous bounds in sync with the steps it actually renders.
  const total = computed(() => toValue(totalSteps));

  const currentStep = computed({
    get: () => modelValue.value,
    set: (value) => {
      modelValue.value = value;
    },
  });

  const stepItems = ref<DialogStepperItemRef[]>([]);

  // Names only count when there is one per step; otherwise the rail falls back to numbered dots.
  const labels = computed(() => {
    const value = toValue(stepLabels);
    return value && value.length === total.value ? value : undefined;
  });
  const interactive = computed(() => toValue(railInteractive) ?? false);
  const variant = computed<DialogStepperRailVariant>(() => toValue(railVariant) ?? "auto");

  /** Jumps back to a completed step from the rail. Forward is only reached through a step's own guard. */
  function goToStep(index: number) {
    if (!interactive.value || index < 0 || index >= currentStep.value) {
      return;
    }
    currentStep.value = index;
  }

  function registerStep(step: DialogStepperItemRef) {
    stepItems.value.push(step);
  }

  function unregisterStep(stepIndex: number) {
    stepItems.value = stepItems.value.filter(step => step.stepIndex !== stepIndex);
  }

  function nextStep() {
    if (currentStep.value < total.value - 1) {
      currentStep.value++;
    }
  }

  function previousStep() {
    if (currentStep.value > 0) {
      currentStep.value--;
    }
  }

  return {
    currentStep,
    totalSteps: total,
    stepItems,
    stepLabels: labels,
    railInteractive: interactive,
    railVariant: variant,
    goToStep,
    registerStep,
    unregisterStep,
    nextStep,
    previousStep,
  };
});

export { useProvideDialogStepperState };

export function useDialogStepperState() {
  const state = useDialogStepperStateRaw();

  if (state == null) {
    throw new Error("Please call `useProvideDialogStepperState` on the appropriate parent component");
  }

  return state;
}
